import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as d3 from 'd3';

export default function KeywordCloud({
  items = [],
  className = '',
  maxWords = 60,
  maxWordsCollapsed = null, // 확대 전 maxWords (null이면 maxWords 사용)
  maxWordsExpanded = 0, // 확대 후 maxWords (0이면 전체 표시)
  onSelect,
  rotateFn,
  variant = 'bluePoster',
}) {
  const containerRef = useRef(null);
  const modalContainerRef = useRef(null);
  const lastDimensionsRef = useRef({ width: 0, height: 0 });
  const isFullscreenRef = useRef(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  isFullscreenRef.current = isFullscreen;

  // 확대/축소 상태에 따라 동적으로 maxWords 결정
  const effectiveMaxWords = useMemo(() => {
    if (isFullscreen) {
      return maxWordsExpanded;
    } else {
      return maxWordsCollapsed !== null ? maxWordsCollapsed : maxWords;
    }
  }, [isFullscreen, maxWordsCollapsed, maxWordsExpanded, maxWords]);

  // 0) body overflow 제어
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  // maxWords를 우선적으로 사용하여 보여줄 개수 결정
  const computeVisibleCount = (totalLen, maxWords) => {
    if (!totalLen) return 0;
    // maxWords가 설정되어 있으면 그 값을 사용, 없으면 전체 사용
    return maxWords > 0 ? Math.min(maxWords, totalLen) : totalLen;
  };

  // 1) 데이터 정규화 + 상위 N개만 사용 (항상 value 내림차순 prefix)
  const words = useMemo(() => {
    const arr = Array.isArray(items) ? items : [];

    const normalized = arr
      .map((d) => {
        const label = (d?.keyword ?? d?.label ?? '').toString().trim();
        if (!label) return null;

        const raw = d?.count ?? d?.value ?? 1;
        const value = Number.isFinite(Number(raw)) ? Number(raw) : 1;
        return { label, value };
      })
      .filter(Boolean);

    if (!normalized.length) return [];

    normalized.sort(
      (a, b) => (b.value - a.value) || a.label.localeCompare(b.label),
    );

    const visibleCount = computeVisibleCount(
      normalized.length,
      effectiveMaxWords,
    );

    const top = normalized.slice(0, visibleCount);
    return top.map((d, idx) => ({ ...d, idx }));
  }, [items, effectiveMaxWords]);

  // 2) ResizeObserver: 일반 모드에서는 width만 바뀔 때만 업데이트 (그리기로 인한 height 변화로 인한 무한 리렌더 방지)
  useEffect(() => {
    const el = isFullscreen ? modalContainerRef.current : containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.round(entry.contentRect.width);
        const h = Math.round(entry.contentRect.height);
        if (w <= 0) continue;
        const prev = lastDimensionsRef.current;
        const fullscreen = isFullscreenRef.current;
        // 일반 모드: width만 바뀔 때만 setState (SVG 그리기로 컨테이너 height가 바뀌는 것을 무시)
        if (!fullscreen) {
          if (prev.width === w) continue;
        } else {
          if (prev.width === w && prev.height === h) continue;
        }
        lastDimensionsRef.current = { width: w, height: h };
        setDimensions({ width: w, height: h });
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [isFullscreen]);

  // 3) 실제 그리기
  useEffect(() => {
    const root = isFullscreen ? modalContainerRef.current : containerRef.current;
    if (!root) return;
    const { width, height: containerHeight } = dimensions;
    if (!width || !words.length) return;

    // width에 따라 최소 높이 조정 (높이값 줄임)
    const minH = width < 200 ? Math.max(80, words.length * 18) : 160;
    // 키워드 개수에 따라 height를 동적으로 계산 (높이값 줄임)
    const baseHeight = width < 200 ? words.length * 18 : Math.round(width * 0.3);
    const wordCountFactor = Math.ceil(words.length / 30); // 30개당 높이 증가
    const maxH = width < 200 ? words.length * 22 : Math.max(350, baseHeight * wordCountFactor);
    const density = Math.min(1, words.length / 20);

    // 부모 컨테이너의 height가 명시적으로 지정되어 있으면 (containerHeight > 0), 
    // 그 높이를 우선적으로 고려하되 너무 작지 않게 minH를 적용함.
    let targetHeight = width < 200
      ? Math.max(minH, words.length * 20)
      : Math.max(
        minH,
        Math.min(maxH, Math.round(width * (0.25 + 0.1 * density * wordCountFactor)))
      );

    if (!isFullscreen && containerHeight > 80) {
      targetHeight = containerHeight;
    }

    root.innerHTML = '';

    const svg = d3
      .select(root)
      .append('svg')
      .attr('width', '100%')
      .attr('height', targetHeight)
      .attr('viewBox', `0 0 ${width} ${targetHeight}`)
      .attr('role', 'img')
      .attr('aria-label', 'Keyword cloud');

    const g = svg.append('g');

    const values = words.map((d) => d.value || 1);
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);

    // width와 키워드 개수, 그리고 targetHeight에 따라 폰트 범위 조정 (글자 크기 키움)
    const fontScaleFactor = Math.max(0.7, words.length / 50);
    const heightFactor = Math.min(1, targetHeight / 400); // 높이가 낮을수록 폰트 축소

    // 글자 크기를 키우기 위해 기본값 증가
    let minFont = Math.max(12, 22 / fontScaleFactor) * heightFactor;
    let maxFont = Math.max(24, 72 / fontScaleFactor) * heightFactor;

    if (width < 150) {
      minFont = Math.max(10, 14 / fontScaleFactor);
      maxFont = Math.max(16, 22 / fontScaleFactor);
    } else if (width < 200) {
      minFont = Math.max(10, 16 / fontScaleFactor);
      maxFont = Math.max(20, 28 / fontScaleFactor);
    } else if (width < 360) {
      minFont = Math.max(12, 18 / fontScaleFactor);
      maxFont = Math.max(24, 36 / fontScaleFactor);
    }

    const sizeScale = d3
      .scaleLinear()
      .domain([minVal, maxVal === minVal ? minVal + 1 : maxVal])
      .range([minFont, maxFont]);

    const cx = width / 2;
    const cy = targetHeight / 2;
    const placed = [];
    const pad = 1; // 단어 간 여백 최소화
    const margin = 0; // 좌우 여백 제거
    const maxTextWidthStrict = width - pad * 2; // margin 제거로 더 넓은 공간 활용
    const boundaryMargin = 0; // 경계 여백 제거

    const collides = (rect) =>
      placed.some(
        (p) =>
          !(
            rect.x2 < p.x1 ||
            rect.x1 > p.x2 ||
            rect.y2 < p.y1 ||
            rect.y1 > p.y2
          ),
      );

    const measureText = (text, fontSize, fontWeight, rotateDeg) => {
      const temp = g
        .append('text')
        .attr('x', -9999)
        .attr('y', -9999)
        .style('font-size', `${fontSize}px`)
        .style('font-weight', fontWeight)
        .style(
          'font-family',
          'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        )
        .attr('transform', rotateDeg ? `rotate(${rotateDeg})` : null)
        .text(text);

      const box = temp.node().getBBox();
      temp.remove();
      return box;
    };

    const getColor = (idx) => {
      if (variant === 'bluePoster') {
        if (idx === 0) return '#2035C6';
        if (idx <= 3) return '#484BFC';
        if (idx <= 7) return '#689EEF';
        if (idx % 3 === 0) return '#9BABC2';
        return '#B3BFD1';
      }
      return '#689EEF';
    };

    const getRotate = (i, w) => {
      if (variant === 'bluePoster') return 0;
      if (typeof rotateFn === 'function') return rotateFn(i, w);
      const set = [0, -20, 15, -10, 10];
      return set[i % set.length];
    };

    // 스파이럴 레이아웃 (maxIter로 반복 제한, 무한 루프 방지)
    // step·angleStep 작게 → 중심부에서 더 촘촘히 채운 뒤 바깥으로
    const step = width > 800 ? 4 : width < 150 ? 2 : 3;
    const angleStep = width < 150 ? 0.12 : 0.10;
    const maxIter = Math.min(4000, Math.max(1200, words.length * 50));

    for (let i = 0; i < words.length; i++) {
      const w = words[i];

      const baseSize = sizeScale(w.value || 1);
      let fontSize = i === 0
        ? (width < 150 ? baseSize + 2 : baseSize + 6)
        : baseSize;
      const fontWeight = i === 0 ? 800 : 600;
      const color = getColor(i);
      const rot = getRotate(i, w);

      let bbox0 = measureText(w.label, fontSize, fontWeight, rot);

      const minFontSize = width < 150 ? 7 : 8;
      if (bbox0.width > maxTextWidthStrict) {
        let safeGuard = 0;
        while (
          bbox0.width > maxTextWidthStrict &&
          fontSize > minFontSize &&
          safeGuard < 15
        ) {
          fontSize = Math.max(minFontSize, fontSize * 0.88);
          bbox0 = measureText(w.label, fontSize, fontWeight, rot);
          safeGuard++;
        }
      }

      let angle = 0;
      let radius = 0;
      let found = null;

      for (let iter = 0; iter < maxIter; iter++) {
        const x = i === 0 && iter === 0 ? cx : cx + radius * Math.cos(angle);
        const y = i === 0 && iter === 0 ? cy : cy + radius * Math.sin(angle);

        const rect = {
          x1: x - bbox0.width / 2 - pad,
          y1: y - bbox0.height / 2 - pad,
          x2: x + bbox0.width / 2 + pad,
          y2: y + bbox0.height / 2 + pad,
        };

        // 경계 체크: 좌우 여백 없이 끝까지 사용, 상하는 최소 여백만 유지
        if (
          rect.x1 < 0 ||
          rect.x2 > width ||
          rect.y1 < 2 ||
          rect.y2 > targetHeight - 2 ||
          collides(rect)
        ) {
          angle += angleStep;
          radius += step * angleStep;
          continue;
        }

        found = { x, y, rect };
        break;
      }

      if (!found) break;

      placed.push(found.rect);

      const text = g
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('transform', `translate(${found.x},${found.y}) rotate(${rot})`)
        .style('font-size', `${fontSize}px`)
        .style('font-weight', fontWeight)
        .style('fill', color)
        .style('cursor', 'pointer')
        .attr('opacity', 0)
        .text(w.label);

      text
        .transition()
        .duration(300)
        .attr('opacity', 1);

      text
        .on('click', () => {
          if (onSelect) {
            onSelect(w);
          } else {
            const homeUrl = (typeof rmVars !== 'undefined' && rmVars.homeUrl) ? rmVars.homeUrl : '';
            const url = `${homeUrl}/publications/?keyword=${encodeURIComponent(w.label)}`;
            window.open(url, '_blank');
          }
        })
        .on('mouseenter', function () {
          d3
            .select(this)
            .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.18))');
        })
        .on('mouseleave', function () {
          d3.select(this).style('filter', 'none');
        });
    }

    // 그룹 위치: 가로 중앙, 세로는 상단 정렬 (위아래 여백 최소화)
    try {
      const bbox = g.node().getBBox();
      const tx = (width - bbox.width) / 2 - bbox.x;
      const padTop = 4; // 여백 줄임
      const padBottom = 4; // 여백 줄임
      const ty = -bbox.y + padTop;
      g.attr('transform', `translate(${tx},${ty})`);

      // 실제 콘텐츠 높이만 사용 (위아래 여백 최소화)
      const actualHeight = bbox.height + padTop + padBottom;
      svg.attr('height', actualHeight);
      svg.attr('viewBox', `0 0 ${width} ${actualHeight}`);

      // 팝업이 열릴 때 콘텐츠 중앙으로 스크롤 이동
      if (isFullscreen && root === modalContainerRef.current) {
        setTimeout(() => {
          const parent = root.parentElement;
          if (parent) {
            parent.scrollTo({
              top: (actualHeight - parent.clientHeight) / 2,
              left: (width - parent.clientWidth) / 2,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    } catch {
      // ignore
    }
  }, [dimensions, words, onSelect, rotateFn, variant, isFullscreen]);

  if (!words.length) return null;

  return (
    <>
      <div className="relative group w-full h-full">
        <div
          ref={containerRef}
          className={[
            'relative overflow-hidden rounded-[24px] bg-white border border-[#E0E7FF] w-full h-full',
            'shadow-[0_18px_45px_rgba(15,35,95,0.10)] py-2 px-2',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
        />
        {/* 크게 보기 버튼 */}
        <button
          type="button"
          onClick={() => {
            setDimensions({ width: 0, height: 0 }); // 강제 재계산 유도
            setIsFullscreen(true);
          }}
          className="absolute top-4 right-4 z-30 w-10 h-10 bg-white border border-[#E4E8F1] rounded-lg flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors opacity-0 group-hover:opacity-100"
          title="크게 보기"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 3H21V9" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 21H3V15" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M21 3L14 10" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 21L10 14" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* 레이어 팝업 (모달) */}
      {isFullscreen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => {
              setDimensions({ width: 0, height: 0 });
              setIsFullscreen(false);
            }}
          />
          <div className="relative w-full h-full lg:w-[90%] lg:h-[80%] bg-white lg:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E4E8F1]">
              <h3 className="text-[18px] font-bold text-[#2A2E36]">Keyword Cloud (Full View)</h3>
              <button
                type="button"
                onClick={() => {
                  setDimensions({ width: 0, height: 0 });
                  setIsFullscreen(false);
                }}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 14H10V20" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M20 10H14V4" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M14 10L21 3" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M10 14L3 21" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            <div className="flex-1 relative bg-white overflow-auto p-4">
              <div ref={modalContainerRef} className="w-full h-full min-h-[500px]" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
